import {useMessages} from "i18n";
import {limitCardTitleKey, type DownloadLimitCardModel} from "./limitCard";
import {SlideOutCard} from "./SlideOutCard";
import type {SlideOutCardControls} from "./useSlideOutCards";
import {Bar, Count, Meter} from "./DownloadLimitCard.styles";

export interface DownloadLimitCardProps {
	/** From {@link downloadLimitCard}; null renders nothing. */
	model: DownloadLimitCardModel | null;
	/** Open or tucked, and the lift: owned by the panel through `useSlideOutCards`, never held here. */
	controls: SlideOutCardControls;
	/** The ONE call to action's label: the plan that lifts the cap, the cap discount, or the referred friend's gift. */
	ctaLabel: string;
	/** The discounted label's accessible name, carrying the renewal price the print has no room for (ui#2545). */
	ctaAria?: string;
	onAction: () => void;
}

/** The warning the card carries in its chip, in the open card's row and on the tucked chip alike. */
function WarningGlyph() {
	return (
		<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M12 9v4"/><path d="M12 17h.01"/>
			<path d="M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0z"/>
		</svg>
	);
}

/**
 * The download-limit card (kitecorp/transferify-ui#2468): the warning member of the send screen's
 * slide-out family.
 *
 * Everything about the shell — the slide, the tucked chip, the ×, the stack placement — belongs to
 * {@link SlideOutCard}, which the upsell card (ui#2278) shares. What lives here is what makes this card
 * the WARNING: its amber accent, its meter, and the usage line under the title.
 */
export function DownloadLimitCard({model, controls, ctaLabel, ctaAria, onAction}: DownloadLimitCardProps) {
	const {sender, common} = useMessages();
	const copy = sender.limitCard;
	if (model === null) {
		return null;
	}
	return (
		<SlideOutCard
			accent="warning"
			open={controls.open}
			lifting={controls.lifting}
			offsetTop={controls.offsetTop}
			stacked={controls.stacked}
			onHeight={controls.onHeight}
			onOpen={controls.openCard}
			onTuck={controls.closeCard}
			testId="download-limit-card"
			data-limit-state={model.state}
			title={copy[limitCardTitleKey(model.state)]}
			showLabel={copy.show}
			tuckLabel={common.tuckTag}
			glyph={<WarningGlyph/>}
			actionLabel={ctaLabel}
			actionAria={ctaAria}
			onAction={onAction}
		>
			<Count>{copy.usedLine(model.used, model.limit)}</Count>
			<Meter
				role="meter"
				aria-label={copy.meterAria}
				aria-valuemin={0}
				aria-valuemax={model.limit}
				aria-valuenow={Math.min(model.used, model.limit)}
				data-pending-downloads={model.recipients - model.shortfall}
				data-shortfall={model.shortfall}
			>
				<Bar $percent={model.usedPercent}/>
				<Bar $percent={model.pendingPercent} $pending/>
			</Meter>
		</SlideOutCard>
	);
}
